import React from "react";
import { Shield, Lock, Eye, FileText, Phone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy & HIPAA Notice</h1>
          <p className="text-lg text-gray-600">
            Your privacy and the security of your health information are our top priorities
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* HIPAA Compliance Overview */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-6 w-6 text-blue-600" />
              <span>HIPAA Compliance Commitment</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Quality Transport Services is committed to protecting your Protected Health Information (PHI) 
              in accordance with the Health Insurance Portability and Accountability Act (HIPAA). We maintain 
              strict policies and procedures to safeguard your medical information and ensure confidential handling 
              of all transportation-related health data.
            </p>
          </CardContent>
        </Card>

        {/* Privacy Sections */}
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 text-blue-600 mr-2" />
              Information We Collect
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Personal Information:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Name, address, phone number, and email address</li>
                <li>• Date of birth and emergency contact information</li>
                <li>• Transportation pickup and destination addresses</li>
                <li>• Mobility needs and medical equipment requirements</li>
              </ul>
              
              <h3 className="font-semibold text-gray-900 mb-3">Protected Health Information (PHI):</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Insurance information and coverage details</li>
                <li>• Medical appointment information and healthcare provider details</li>
                <li>• Mobility assistance needs and medical equipment specifications</li>
                <li>• Any health-related information necessary for safe transportation</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Technical Information:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Website usage data and IP addresses (non-PHI)</li>
                <li>• Browser information for website functionality</li>
                <li>• Cookies for session management (no PHI stored)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 text-blue-600 mr-2" />
              How We Protect Your Information
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Technical Safeguards:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• SSL encryption for all data transmission</li>
                <li>• Secure servers with restricted access controls</li>
                <li>• Regular security audits and vulnerability assessments</li>
                <li>• Encrypted storage of sensitive information</li>
                <li>• Secure backup systems with encryption</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Administrative Safeguards:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• HIPAA-trained staff with signed confidentiality agreements</li>
                <li>• Role-based access controls limiting PHI access to authorized personnel</li>
                <li>• Regular staff training on privacy and security procedures</li>
                <li>• Incident response procedures for potential breaches</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Physical Safeguards:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Secured facilities with restricted physical access</li>
                <li>• Locked file cabinets for any physical health records</li>
                <li>• Secure disposal of PHI-containing materials</li>
                <li>• Vehicle security measures to protect client information during transport</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 text-blue-600 mr-2" />
              How We Use Your Information
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Transportation Services:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Scheduling and coordinating your transportation</li>
                <li>• Ensuring appropriate vehicle and equipment for your needs</li>
                <li>• Communicating with healthcare providers as necessary for service delivery</li>
                <li>• Providing appointment reminders and service updates</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Insurance and Billing:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Processing insurance claims for covered transportation</li>
                <li>• Verifying coverage and obtaining prior authorizations when required</li>
                <li>• Maintaining records for audit and compliance purposes</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Legal and Safety Requirements:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Compliance with state and federal transportation regulations</li>
                <li>• Emergency response and safety protocols</li>
                <li>• Quality assurance and service improvement</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights Under HIPAA</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">You have the following rights regarding your Protected Health Information:</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900">Right to Access:</h4>
                  <p className="text-gray-700">Request copies of your health information in our records</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Right to Amendment:</h4>
                  <p className="text-gray-700">Request corrections to your health information if you believe it is incorrect or incomplete</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Right to Restrict:</h4>
                  <p className="text-gray-700">Request restrictions on how we use or disclose your health information</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Right to Confidential Communications:</h4>
                  <p className="text-gray-700">Request that we communicate with you in a certain way or at certain locations</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Right to Accounting:</h4>
                  <p className="text-gray-700">Receive a list of disclosures we have made of your health information</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">Right to Notification:</h4>
                  <p className="text-gray-700">Be notified if there is a breach of your unsecured health information</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing and Disclosures</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">We may share your information in the following circumstances:</p>
              
              <h3 className="font-semibold text-gray-900 mb-3">With Your Authorization:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Healthcare providers involved in your care</li>
                <li>• Insurance companies for coverage verification and claims processing</li>
                <li>• Family members or caregivers you designate</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Without Authorization (as permitted by law):</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Emergency situations to ensure your safety</li>
                <li>• Legal requirements or court orders</li>
                <li>• Public health and safety reporting as required by law</li>
                <li>• Business associates who assist with our services (under signed agreements)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information for Privacy Concerns</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Privacy Officer:</h3>
                  <p className="text-gray-700">Quality Transport Services</p>
                  <p className="text-gray-700">Privacy Officer</p>
                  <div className="flex items-center space-x-2 text-gray-700 mt-2">
                    <Phone className="h-4 w-4" />
                    <span>(269) 248-3514</span>
                  </div>
                  <p className="text-gray-700">qualitytransportservices42@gmail.com</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">File a Complaint:</h3>
                  <p className="text-gray-700 mb-2">If you believe your privacy rights have been violated, you may file a complaint with:</p>
                  <p className="text-gray-700">U.S. Department of Health and Human Services</p>
                  <p className="text-gray-700">Office for Civil Rights</p>
                  <p className="text-gray-700">www.hhs.gov/ocr/privacy/hipaa/complaints</p>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-white rounded border">
                <p className="text-sm text-gray-600">
                  <strong>Important:</strong> You will not be retaliated against for filing a complaint. 
                  We encourage you to contact us first so we can address your concerns directly.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700">
                We reserve the right to change this privacy policy and our privacy practices. 
                Any changes will be posted on our website with the effective date. We will notify 
                clients of significant changes by mail or email. You may request a copy of our 
                current privacy policy at any time by contacting us.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Effective Date</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700">
                This Privacy Policy is effective as of {new Date().toLocaleDateString()} and applies to all 
                information collected by Quality Transport Services before and after this date.
              </p>
            </div>
          </section>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Questions About Your Privacy?</h3>
          <p className="text-blue-100 mb-6">
            Our privacy officer is available to answer any questions about how we protect your health information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:(269) 248-3514" className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
              Call (269) 248-3514
            </a>
            <a href="mailto:qualitytransportservices42@gmail.com" className="border border-white text-white hover:bg-white hover:text-blue-900 px-6 py-3 rounded-lg font-semibold transition-colors">
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;